import { editsOf, value, values } from '../../parser/fortigate'
import type { FgConfig } from '../../parser/types'
import { finding, type Finding, type Rule } from '../types'

/** A firewall policy reduced to the fields the rules reason about. */
export interface PolicyView {
  key: string
  line: number
  name: string | undefined
  srcintf: string[]
  dstintf: string[]
  srcaddr: string[]
  dstaddr: string[]
  service: string[]
  /** FortiOS treats an unset action as deny. */
  action: string
  /** FortiOS treats an unset status as enable. */
  enabled: boolean
  logtraffic: string | undefined
}

export function policies(config: FgConfig): PolicyView[] {
  return editsOf(config, 'firewall', 'policy').map((p) => ({
    key: p.key,
    line: p.line,
    // An unset name must not render as `Policy 1 ("")`.
    name: p.sets['name']?.join(' ') || undefined,
    srcintf: lower(values(p, 'srcintf')),
    dstintf: lower(values(p, 'dstintf')),
    srcaddr: lower(values(p, 'srcaddr')),
    dstaddr: lower(values(p, 'dstaddr')),
    service: lower(values(p, 'service')),
    action: value(p, 'action') ?? 'deny',
    enabled: value(p, 'status') !== 'disable',
    logtraffic: value(p, 'logtraffic'),
  }))
}

const label = (p: PolicyView): string => (p.name !== undefined ? `${p.key} ("${p.name}")` : p.key)

const isAnySrc = (p: PolicyView): boolean => p.srcaddr.includes('all')
const isAnyDst = (p: PolicyView): boolean => p.dstaddr.includes('all')
const isAnySvc = (p: PolicyView): boolean => p.service.includes('all')

export const permitAnyAnyAny: Rule = {
  id: 'fg-policy-any-any-any',
  title: 'Policy permits any source to any destination on any service',
  run(config) {
    return policies(config)
      .filter((p) => p.enabled && p.action === 'accept' && isAnySrc(p) && isAnyDst(p) && isAnySvc(p))
      .map((p) =>
        finding(permitAnyAnyAny, {
          severity: 'critical',
          title: `Policy ${label(p)} permits all traffic`,
          detail:
            `Policy ${p.key} accepts traffic with srcaddr all, dstaddr all and service ALL from ` +
            `${p.srcintf.join(', ') || 'any interface'} to ${p.dstintf.join(', ') || 'any interface'}. ` +
            'It imposes no restriction of any kind, and every policy below it on the same interface pair ' +
            'is unreachable.',
          location: `firewall policy ${p.key}`,
          line: p.line,
          pci: '1.2.1',
          remediation:
            `config firewall policy\n    edit ${p.key}\n` +
            '        set srcaddr "<specific-source>"\n' +
            '        set dstaddr "<specific-destination>"\n' +
            '        set service "HTTPS" "DNS"\n    next\nend',
        }),
      )
  },
}

export const permitAnyService: Rule = {
  id: 'fg-policy-any-service',
  title: 'Policy permits every service',
  run(config) {
    return policies(config)
      .filter(
        (p) =>
          p.enabled &&
          p.action === 'accept' &&
          isAnySvc(p) &&
          // The any/any/any case is reported once, by its own rule.
          !(isAnySrc(p) && isAnyDst(p)),
      )
      .map((p) =>
        finding(permitAnyService, {
          severity: 'high',
          title: `Policy ${label(p)} allows every service`,
          detail:
            `Policy ${p.key} sets service to ALL, so every TCP and UDP port and every protocol is ` +
            'permitted between the addresses it matches. Compromise of one host on either side turns ' +
            'the whole port range into a lateral-movement path.',
          location: `firewall policy ${p.key}`,
          line: p.line,
          pci: '1.2.1',
          remediation:
            `config firewall policy\n    edit ${p.key}\n` +
            '        set service "HTTPS" "DNS"\n    next\nend',
        }),
      )
  },
}

export const permitAnySource: Rule = {
  id: 'fg-policy-any-source',
  title: 'Policy accepts traffic from any source',
  run(config) {
    return policies(config)
      // `!isAnySvc` alone excludes the any/any/any case, which has its own rule, and also
      // the any-service case, which has its own. What is left is the finding this rule owns.
      .filter((p) => p.enabled && p.action === 'accept' && isAnySrc(p) && !isAnySvc(p))
      .map((p) =>
        finding(permitAnySource, {
          severity: 'medium',
          title: `Policy ${label(p)} accepts any source address`,
          detail:
            `Policy ${p.key} uses srcaddr all. If ${p.srcintf.join(', ') || 'the source interface'} ` +
            'touches an untrusted network, this policy is reachable by anyone who can route to it.',
          location: `firewall policy ${p.key}`,
          line: p.line,
          pci: '1.2.1',
          remediation:
            `config firewall policy\n    edit ${p.key}\n        set srcaddr "<specific-source>"\n    next\nend`,
        }),
      )
  },
}

export const acceptWithoutLogging: Rule = {
  id: 'fg-policy-no-logging',
  title: 'Accept policy with logging disabled',
  run(config) {
    return policies(config)
      .filter((p) => p.enabled && p.action === 'accept' && p.logtraffic === 'disable')
      .map((p) =>
        finding(acceptWithoutLogging, {
          severity: 'medium',
          title: `Policy ${label(p)} permits traffic without logging it`,
          detail:
            `Policy ${p.key} sets logtraffic to disable. Traffic it permits leaves no record, so an ` +
            'incident that used this path cannot be reconstructed afterwards.',
          location: `firewall policy ${p.key}`,
          line: p.line,
          pci: '10.2.1',
          remediation:
            `config firewall policy\n    edit ${p.key}\n        set logtraffic all\n    next\nend`,
        }),
      )
  },
}

export const disabledPolicy: Rule = {
  id: 'fg-policy-disabled',
  title: 'Disabled policy left in the ruleset',
  run(config) {
    return policies(config)
      .filter((p) => !p.enabled)
      .map((p) =>
        finding(disabledPolicy, {
          severity: 'info',
          title: `Policy ${label(p)} is disabled but still present`,
          detail:
            `Policy ${p.key} has status disable. It has no effect today, but it is one command away ` +
            'from taking effect and it makes the ruleset harder to review correctly.',
          location: `firewall policy ${p.key}`,
          line: p.line,
          pci: '1.2.7',
          remediation:
            `# Confirm it is genuinely unwanted, then:\nconfig firewall policy\n    delete ${p.key}\nend`,
        }),
      )
  },
}

/**
 * Reports policies that can never match because an earlier policy already covers
 * everything they would.
 *
 * This is the check that is genuinely hard to do by eye: it is quadratic in the number
 * of policies, and on a ruleset of any age the shadowed entry usually looks perfectly
 * reasonable in isolation.
 *
 * Coverage is compared by object NAME, not by resolved address range — `all` and `ALL`
 * are treated as universal, and otherwise a policy covers another only when its object
 * set is a superset. That under-reports (two differently-named objects may hold the same
 * subnet) and never over-reports, which is the right direction: a false "this rule is
 * dead" would get a working rule deleted.
 */
export const shadowedPolicy: Rule = {
  id: 'fg-policy-shadowed',
  title: 'Policy unreachable because an earlier policy covers it',
  run(config) {
    const all = policies(config).filter((p) => p.enabled)
    const out: Finding[] = []

    for (let i = 0; i < all.length; i++) {
      const later = all[i]!
      for (let j = 0; j < i; j++) {
        const earlier = all[j]!
        if (!covers(earlier, later)) continue

        out.push(
          finding(shadowedPolicy, {
            severity: 'medium',
            title: `Policy ${label(later)} never matches`,
            detail:
              `Policy ${earlier.key} appears earlier and already matches everything policy ${later.key} ` +
              `would match, so policy ${later.key} is evaluated but can never take effect. Policy ` +
              `${earlier.key} has action ${earlier.action}` +
              (earlier.action !== later.action
                ? `, while policy ${later.key} has action ${later.action} — the intended behaviour of ` +
                  `policy ${later.key} is not happening.`
                : '.'),
            location: `firewall policy ${later.key}`,
            line: later.line,
            pci: '1.2.7',
            remediation:
              `# Either move policy ${later.key} above policy ${earlier.key}:\n` +
              `config firewall policy\n    move ${later.key} before ${earlier.key}\nend\n` +
              `# or narrow policy ${earlier.key}, or delete policy ${later.key} if it is redundant.`,
          }),
        )
        break // One explanation per shadowed policy is enough to act on.
      }
    }

    return out
  },
}

function covers(earlier: PolicyView, later: PolicyView): boolean {
  return (
    supersetOf(earlier.srcintf, later.srcintf, 'any') &&
    supersetOf(earlier.dstintf, later.dstintf, 'any') &&
    supersetOf(earlier.srcaddr, later.srcaddr, 'all') &&
    supersetOf(earlier.dstaddr, later.dstaddr, 'all') &&
    supersetOf(earlier.service, later.service, 'all')
  )
}

/** True when `outer` matches everything `inner` matches. `wildcard` in `outer` matches all. */
function supersetOf(outer: string[], inner: string[], wildcard: string): boolean {
  if (outer.length === 0 || inner.length === 0) return false
  if (outer.includes(wildcard)) return true
  if (inner.includes(wildcard)) return false
  return inner.every((item) => outer.includes(item))
}

const lower = (list: string[]): string[] => list.map((v) => v.toLowerCase())

export const POLICY_RULES: Rule[] = [
  permitAnyAnyAny,
  permitAnyService,
  permitAnySource,
  acceptWithoutLogging,
  disabledPolicy,
  shadowedPolicy,
]
